import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LogoMark } from '@/components/Logo';

// Remembered for the browser session only: every new visit asks again.
const KEY = 'age_verified';

export const isAgeVerified = () => {
  try {
    return typeof window !== 'undefined' && Boolean(sessionStorage.getItem(KEY));
  } catch {
    return false;
  }
};

// Full-screen overlay. The page content is rendered underneath (and in the
// prerendered HTML) so crawlers can read it; an inline script in index.html
// adds `age-ok` to <html> once confirmed in this session so the gate never flashes.
export const AgeVerification = ({ show, onVerified }) => {
  const { t } = useTranslation();
  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [show]);

  const handleEnter = () => {
    try {
      sessionStorage.setItem(KEY, 'true');
    } catch {
      // storage blocked — still let the visitor in for this session
    }
    document.documentElement.classList.add('age-ok');
    onVerified();
  };

  const handleLeave = () => {
    window.location.href = 'https://www.google.com';
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-candy-bg px-4"
      data-age-gate=""
      data-testid="age-verification-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-title"
    >
      <div className="w-full max-w-md rounded-card bg-candy-surface p-8 text-center shadow-[0_30px_60px_-30px_rgb(var(--ink)/0.35)] md:p-10">
        <div className="flex items-center justify-center gap-2 font-heading text-2xl font-extrabold tracking-[-0.03em]">
          <LogoMark />
          Blissful Play
        </div>
        <ul className="mt-6 flex flex-wrap justify-center gap-2">
          <li className="rounded-full bg-candy-yellow px-3.5 py-[7px] text-[13px] font-bold">{t('age.badgeAdults')}</li>
          <li className="rounded-full bg-candy-cyan px-3.5 py-[7px] text-[13px] font-bold">{t('age.badgeDiscreet')}</li>
        </ul>
        <h2 id="age-title" className="mt-5 font-heading text-[32px] font-extrabold leading-none tracking-[-0.035em]">
          {t('age.title')}
        </h2>
        <p className="mt-3 text-candy-ink-muted">{t('age.text')}</p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            data-testid="age-verify-enter-btn"
            onClick={handleEnter}
            className="h-[52px] w-full rounded-full sm:flex-1 bg-candy-ink text-lg font-bold text-white transition-colors hover:bg-candy-ink/90"
          >
            {t('age.enter')}
          </button>
          <button
            type="button"
            data-testid="age-verify-leave-btn"
            onClick={handleLeave}
            className="h-[52px] w-full rounded-full sm:flex-1 border-2 border-candy-ink bg-candy-surface text-lg font-bold transition-colors hover:bg-candy-pink"
          >
            {t('age.leave')}
          </button>
        </div>
      </div>
    </div>
  );
};
